import type { ReactNode } from 'react';
import { Component, useCallback } from 'react';

import { Button, MessageIllustration } from '@snowit/ui';

import Layout from './components/Layout';

type ErrorProps = {
  children: ReactNode;
};

type ErrorState = {
  hasError: boolean;
  error: Error | undefined;
  message: string;
};

type ErrorBoundaryProps = {
  message: string;
};

const ErrorBoundaryComponent = (props: ErrorBoundaryProps) => {
  const { message } = props;

  const handleClick = useCallback(() => {
    const win: Window = window;
    win.location = '/';
    win.location.reload();
  }, []);

  return (
    <Layout>
      <div
        style={{
          maxWidth: 'var(--w-max-content-xs)',
          minHeight: '50vh',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '4rem',
          paddingBottom: '2.5rem',
        }}
      >
        <MessageIllustration image={'img_error_snowman'} title={'Error'} text={message} />
        <Button label={'Reload'} theme={'fill'} bgColor={'primary'} onClick={handleClick} />
      </div>
    </Layout>
  );
};

class ErrorBoundary extends Component<ErrorProps, ErrorState> {
  constructor(props: ErrorProps) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      message: '',
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.setState({
      error,
      message: error.message,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundaryComponent message={this.state.message} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
